import type { Movement, ServiceTask } from '../../types';
import { MoreHorizontal, MapPin, Building2, UserCircle2, Flame, Droplets, Map, ArrowRight, Circle, CheckCircle2, Info, Truck } from 'lucide-react';
import { Button } from '@/components/ui/button';

export function MovementCard({ movement, detailLevel, compact = false }: { movement: Movement, detailLevel: 'L1' | 'L2' | 'L3', compact?: boolean }) {
  const isArrival = movement.direction === 'inbound';
  const hasStacking = movement.isStackingRequired;
  
  // Custom Progress Bar Logic
  const totalServices = movement.services.length;
  const completedServices = movement.services.filter(s => s.status === 'completed').length;
  const inProgressServices = movement.services.filter(s => s.status === 'in_progress').length;
  
  const progressPercent = totalServices > 0 ? (completedServices / totalServices) * 100 : 0;
  const inProgressPercent = totalServices > 0 ? (inProgressServices / totalServices) * 100 : 0;

  const totalMin = movement.services.reduce((acc, s) => acc + s.estimatedMinutes, 0);

  return (
    <div className={`group bg-white rounded-xl border transition-all duration-200 overflow-hidden shadow-xs hover:shadow-md ${hasStacking ? 'border-amber-200 shadow-amber-500/5 cursor-pointer' : 'border-slate-200/70 hover:border-slate-300 cursor-pointer'}`}>
      
      {/* Header Level (L1) */}
      <div className={compact ? "p-3" : "p-4"}>
        
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-baseline gap-3">
             <span className={`${compact ? 'text-lg' : 'text-xl'} font-bold tracking-tight ${isArrival ? 'text-blue-700 font-mono' : 'text-violet-700 font-mono'}`}>
                {movement.scheduledTime}
             </span>
             <span className={`${compact ? 'text-base' : 'text-lg'} font-bold text-slate-800 tracking-tight`}>{movement.aircraft.tailNumber}</span>
             <span className="text-sm font-medium text-slate-400">&middot;</span>
             <span className="text-sm font-semibold text-slate-500">{movement.aircraft.type}</span>
          </div>

          <Button variant="ghost" size="icon" className="h-6 w-6 text-slate-300 hover:text-slate-600 -mr-1 -mt-1 hover:bg-slate-100 transition-colors">
            <MoreHorizontal size={16} />
          </Button>
        </div>

        {/* Progress Bar (L1) */}
        {totalServices > 0 ? (
          <div className={`${compact ? 'mb-3' : 'mb-4'} group-hover:scale-[1.01] transition-transform origin-left`}>
            <div className="flex items-end justify-between mb-1.5">
              <div className="text-[11px] font-bold tracking-wide uppercase text-slate-500 flex items-center gap-1.5">
                {hasStacking && <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />}
                {hasStacking ? 'Stacking Req · ' : ''} {totalMin} Min Est
              </div>
              <div className="text-[10px] font-semibold text-slate-400">{completedServices}/{totalServices} Tasks</div>
            </div>
            <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden flex shadow-inner border border-slate-200/50">
               <div 
                 className={`h-full transition-all duration-500 ease-out ${hasStacking ? 'bg-amber-500' : isArrival ? 'bg-blue-500' : 'bg-violet-500'}`} 
                 style={{ width: `${progressPercent}%` }} 
               />
               <div 
                 className={`h-full transition-all duration-500 ease-out opacity-40 bg-[length:1rem_1rem] bg-[linear-gradient(45deg,rgba(255,255,255,.15)_25%,transparent_25%,transparent_50%,rgba(255,255,255,.15)_50%,rgba(255,255,255,.15)_75%,transparent_75%,transparent)] animate-[progress-stripes_1s_linear_infinite] ${hasStacking ? 'bg-amber-400' : isArrival ? 'bg-blue-400' : 'bg-violet-400'}`} 
                 style={{ width: `${inProgressPercent}%` }} 
               />
            </div>
          </div>
        ) : (
           <div className={compact ? "mb-3" : "mb-4"}>
              <div className="text-[11px] font-bold tracking-wide uppercase text-slate-400 italic">Self Service / No Prep</div>
              <div className="h-2 w-full bg-slate-50 rounded-full border border-slate-100 mt-1.5"></div>
           </div>
        )}

        {/* Info Grid (L1) */}
        <div className={`grid grid-cols-2 gap-x-4 ${compact ? 'gap-y-1.5 mb-3' : 'gap-y-2 mb-4'}`}>
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <div className={`p-1 rounded-md ${isArrival ? 'bg-blue-50 text-blue-500' : 'bg-violet-50 text-violet-500'}`}>
                <MapPin size={14} />
            </div>
            <span className="truncate">{movement.originDestination}</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-slate-600">
             <div className="p-1 rounded-md bg-slate-100 text-slate-500">
                <Building2 size={14} />
            </div>
            <span className="truncate font-medium text-slate-700">{movement.assignedSpace}</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-slate-600 col-span-2">
             <div className="p-1 rounded-md bg-slate-100 text-slate-500">
                <UserCircle2 size={14} />
            </div>
            <span className="truncate font-medium">{movement.clientName}</span>
          </div>
        </div>

        {/* Action Row (L1) */}
        <div className={`flex items-center gap-2 ${compact ? 'mt-1.5 pt-2' : 'mt-2 pt-3'} border-t border-slate-100`}>
           {isArrival ? (
               <>
                 <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white font-medium shadow-sm h-8 px-4 text-xs">
                    Check In
                 </Button>
                 <Button variant="outline" size="sm" className="h-8 px-4 text-xs font-medium text-slate-600 hover:text-slate-900 border-slate-200 shadow-sm">
                    Assign
                 </Button>
               </>
           ) : (
               <>
                 <Button size="sm" className="bg-slate-800 hover:bg-slate-900 text-white font-medium shadow-sm h-8 px-4 text-xs">
                    Begin Prep
                 </Button>
                 <Button variant="outline" size="sm" className="h-8 px-4 text-xs font-medium text-slate-600 hover:text-slate-900 border-slate-200 shadow-sm">
                    Mark Departed
                 </Button>
               </>
           )}
           
           {hasStacking && (
                <Button variant="secondary" size="sm" className={`ml-auto h-8 ${compact ? 'px-2 text-[11px]' : 'px-3 text-xs'} font-bold text-amber-700 bg-amber-100 hover:bg-amber-200 border border-amber-200`}>
                   {compact ? 'Stacking Plan' : 'View Stacking plan'}
                </Button>
           )}
        </div>

      </div>

      {/* Services Sub-Tree (L2) */}
      {(detailLevel === 'L2' || detailLevel === 'L3') && movement.services.length > 0 && (
        <div className="bg-slate-50/80 border-t border-slate-100 px-3 py-2 text-sm">
            <div className="relative border-l-2 border-slate-200/70 ml-3 py-1 pb-2">
                {movement.services.map((service) => (
                    <ServiceSubBranch key={service.id} service={service} detailLevel={detailLevel} />
                ))}
            </div>
        </div>
      )}

    </div>
  );
}

function ServiceSubBranch({ service, detailLevel }: { service: ServiceTask, detailLevel: 'L2' | 'L3' }) {
    
    const getIcon = (type: string) => {
        switch(type) {
            case 'fuel': return <Flame size={12} />;
            case 'tow': return <Map size={12} />;
            case 'deice': return <Droplets size={12} />;
            case 'stacking': return <ArrowRight size={12} />;
            default: return <Truck size={12} />;
        }
    };

    const getStatusIcon = (status: string) => {
        switch(status) {
            case 'completed': return <CheckCircle2 size={16} className="text-emerald-500 fill-emerald-50" />;
            case 'in_progress': return <div className="w-4 h-4 rounded-full border-2 border-blue-500 border-t-transparent animate-spin" />;
            default: return <Circle size={16} className="text-slate-300 fill-white" />;
        }
    }

    const isStacking = service.type === 'stacking';
    
    return (
        <div className="relative group/service mt-1">
            {/* Tree Branch Line */}
            <div className="absolute top-4 -left-[2px] w-4 border-b-2 border-slate-200/70"></div>
            
            <div className="pl-6 py-1.5 flex flex-col justify-center">
                <div className="flex items-center gap-3 pr-2">
                    {/* Status Toggle (Interactive Mock) */}
                    <button className="shrink-0 hover:scale-110 transition-transform active:scale-95 cursor-pointer outline-none flex items-center justify-center">
                        {getStatusIcon(service.status)}
                    </button>
                    
                    {/* Service Info */}
                    <div className="flex-1 min-w-0 flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 truncate">
                            <span className={`flex items-center justify-center w-5 h-5 rounded ${isStacking ? 'bg-red-100 text-red-600' : 'bg-slate-200/70 text-slate-500'}`}>
                                {getIcon(service.type)}
                            </span>
                            <span className={`text-sm font-medium truncate ${isStacking ? 'text-red-700 font-bold' : service.status === 'completed' ? 'text-slate-400 line-through' : 'text-slate-700'}`}>
                                {service.name}
                            </span>
                        </div>
                        {service.estimatedMinutes > 0 && (
                            <span className="shrink-0 text-xs font-mono font-semibold text-slate-400">{service.estimatedMinutes}m</span>
                        )}
                    </div>
                </div>

                {/* Stacking L3 Details */}
                {isStacking && service.stacking && detailLevel === 'L3' && (
                    <div className="mt-2 ml-4 mb-2 pl-3 border-l-2 border-red-200/50 space-y-1.5 py-1">
                        {service.stacking.steps.map((step) => (
                            <div key={step.id} className="flex items-center gap-2 text-xs">
                                <span className="font-bold font-mono text-slate-600 bg-white border border-slate-200 rounded px-1 min-w-16 whitespace-nowrap text-center">{step.aircraft}</span>
                                <span className={`text-slate-500 font-medium ${step.action === 'Pull Out' || step.action === 'Tow In' ? 'text-amber-600' : ''}`}>
                                    {step.action}
                                </span>
                                <span className="text-slate-400">&rarr;</span>
                                <span className="text-slate-700 font-medium truncate">{step.to}</span>
                            </div>
                        ))}
                        {service.stacking.notes.map((note) => (
                            <div key={note} className="flex items-start gap-1.5 text-xs text-slate-500 mt-2 bg-slate-100/50 p-1.5 rounded-md border border-slate-200/50">
                                <Info size={12} className="shrink-0 mt-0.5 text-blue-500" />
                                <span className="leading-tight">{note}</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
